import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/api/notifications';
import type { NotificationQuery, NotificationStatus } from '@/types/notification';

const NOTIFICATIONS_KEY = 'notifications';

/**
 * Fetches the paginated/filtered list of notifications.
 * @param query - Filter/pagination criteria for the notification list.
 * @param enabled - Whether the query should run automatically.
 * @returns TanStack Query result containing the notification list.
 */
export function useNotifications(query: NotificationQuery = {}, enabled = true) {
  return useQuery({
    queryKey: [NOTIFICATIONS_KEY, 'list', query],
    queryFn: () => notificationsApi.list(query),
    enabled,
  });
}

/**
 * Fetches the notification summary (e.g. unseen/critical counts) used for badges.
 * @returns TanStack Query result containing the notification summary.
 */
export function useNotificationsSummary() {
  return useQuery({
    queryKey: [NOTIFICATIONS_KEY, 'summary'],
    queryFn: notificationsApi.summary,
  });
}

/**
 * Marks a notification event as seen and invalidates cached notification data.
 * @returns Mutation for marking a notification event seen, keyed by notification event id.
 */
export function useMarkNotificationSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationEventId: number) => notificationsApi.markSeen(notificationEventId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] }),
  });
}

/**
 * Marks a critical notification item as "in order" (resolved), with an optional note.
 * @returns Mutation accepting `{ criticalItemId, note }`.
 */
export function useMarkCriticalInOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ criticalItemId, note }: { criticalItemId: number; note?: string }) =>
      notificationsApi.markInOrder(criticalItemId, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] }),
  });
}

/**
 * Updates the status of a critical notification item, with an optional note.
 * @returns Mutation accepting `{ criticalItemId, status, note }`.
 */
export function useUpdateNotificationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ criticalItemId, status, note }: { criticalItemId: number; status: NotificationStatus; note?: string }) =>
      notificationsApi.updateStatus(criticalItemId, { status, note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] }),
  });
}

/**
 * Adds a comment to a critical notification item.
 * @returns Mutation accepting `{ criticalItemId, comment }`.
 */
export function useAddNotificationComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ criticalItemId, comment }: { criticalItemId: number; comment: string }) =>
      notificationsApi.addComment(criticalItemId, comment),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] }),
  });
}

/**
 * Fetches the status/comment history for a single critical notification item.
 * @param criticalItemId - Id of the critical item, or null when none is selected.
 * @param enabled - Whether the query should run automatically.
 * @returns TanStack Query result containing the item's history.
 */
export function useNotificationHistory(criticalItemId: number | null, enabled = true) {
  return useQuery({
    queryKey: [NOTIFICATIONS_KEY, 'history', criticalItemId],
    queryFn: () => notificationsApi.history(criticalItemId as number),
    enabled: enabled && criticalItemId !== null,
  });
}
